import { lazy } from "react";
import { Navigate, useLocation, Route } from "react-router-dom";
import FirstMileLayout from "src/_modules/layouts/FirstMile";
import LazyRouteWrapper from "../LazyRouteWrapper";

const Dashboard = lazy(() => import("src/views/Dashboard"));
const LanguageSetting = lazy(() => import("src/modules/Settings/LanguageSetting"));
const WhatsappMessagesSettings = lazy(() => import("src/modules/Settings/WhatsappMessagesSettings"));
const DriverList = lazy(() => import('src/_modules/Users/Driver/DriverList'));
const AddDriver = lazy(() => import('src/_modules/Users/Driver/DriverManagement'));
const DriverDetails = lazy(() => import("src/_modules/Users/Driver/DriverDetails"));
const ZoneList = lazy(() => import("src/views/ZoneList"));
const TrackDrivers = lazy(() => import("src/_modules/Users/Driver/TrackDrivers"));
const RecentOrders = lazy(() => import("src/modules/Order/OrdersFilter/RecentOrders.js"));
const Warehouse = lazy(() => import("src/modules/Warehouse"));
const WarehouseGuard = lazy(() => import("src/_modules/Guard/GuardManagements"));
const FirstMileDashboard = lazy(() => import("src/_modules/Dashboard"));
const RecentTasks = lazy(() => import("src/_modules/Tasks/TasksFilter/RecentTasks"));
const FirstMileCreateTask = lazy(() => import("src/_modules/Tasks/FirstMile/CreateTask"));
const ZonesTasksCount = lazy(() => import("src/_modules/Tasks/FirstMile/ZonesTasksCount"));
const ZoneTasksList = lazy(() => import("src/_modules/Tasks/FirstMile/ZoneTasksList"));
const TasksList = lazy(() => import("src/_modules/Tasks/FirstMile/TasksList"));
const WarehousesOrdersCount = lazy(() => import("src/_modules/Tasks/FirstMile/WarehousesOrdersCount"));
const WarehouseOrdersList = lazy(() => import("src/_modules/Tasks/FirstMile/WarehouseOrdersList"));
const TaskDetails = lazy(() => import("src/_modules/Tasks/FirstMile/TaskDetails"));
const OrdersList = lazy(() => import("src/_modules/Tasks/FirstMile/OrdersList"));
const OrderDetails = lazy(() => import("src/_modules/Tasks/FirstMile/OrderDetails"));
const AccountPlans = lazy(() => import("src/_modules/Account/AccountPlans"));
const AccountSubscription = lazy(() => import("src/_modules/Account/AccountSubscription"));
const MerchantAccountBasicInfo = lazy(() => import("src/_modules/Account/MerchantAccountBasicInfo"));
const MerchantAccountSettings = lazy(() => import("src/_modules/Account/MerchantAccountSettings"));
const PlanCheckout = lazy(() => import("src/_modules/Account/PlanCheckout"));
const WarehouseOrdersZone = lazy(() => import("src/_modules/Tasks/ShippingCompany/WarehouseOrdersZone"));
const ZoneOrders = lazy(() => import("src/_modules/Tasks/ShippingCompany/ZoneOrders"));
const ZoneOrdersCount = lazy(() => import("src/_modules/Tasks/ShippingCompany/ZoneOrdersCount"));
const Users = lazy(() => import("src/_modules/Users/Users/Users"));
const Profile = lazy(() => import("src/_modules/Users/Profile"));
const Customizations = lazy(() => import("src/_modules/Customizations/Customizations"));
const NotFound = lazy(() => import("src/_modules/common/NotFound"));
const AccountBalance = lazy(() => import("src/_modules/MapitShip/views/AccountBalance"));
const AdminOrdersFilter = lazy(() => import("src/modules/Order/OrdersFilter/AdminOrdersFilter"));
const AdminTasksFilter = lazy(() => import("src/_modules/Tasks/TasksFilter/AdminTasksFilter"));

const CheckBeforeEnterMerchant = (currentUser) => {
    const location = useLocation()
    if (location.pathname !== "/" && currentUser?.merchant?.currentStatus === "BLOCKED") {
        return <Navigate to="/blocked" />
    } else if (currentUser) {
        return <FirstMileLayout />
    }
    return <Navigate to="/auth/general" />
}

const firstMileRoutes = (currentUser) => (
    <>
        <Route path="merchant" element={CheckBeforeEnterMerchant(currentUser)}>
            <Route path="" element={<LazyRouteWrapper><FirstMileDashboard /></LazyRouteWrapper>} />
            <Route path="orders">
                <Route path="warehouse_orders_zone" element={<LazyRouteWrapper><WarehouseOrdersZone /></LazyRouteWrapper>} />
                <Route path="filter" element={<LazyRouteWrapper><AdminOrdersFilter key="orders-filter" /></LazyRouteWrapper>} />
                <Route path="*" element={<Navigate to="/errors/404" />} />
            </Route>
            <Route path="users" element={<LazyRouteWrapper><Users /></LazyRouteWrapper>} />
            <Route path="drivers">
                <Route path="add" element={<LazyRouteWrapper><AddDriver /></LazyRouteWrapper>} />
                <Route path="" element={<LazyRouteWrapper><DriverList /></LazyRouteWrapper>} />
                <Route path=":id" element={<LazyRouteWrapper><DriverDetails /></LazyRouteWrapper>} />
                <Route path="track" element={<LazyRouteWrapper><TrackDrivers /></LazyRouteWrapper>} />
                <Route path="*" element={<Navigate to="/errors/404" />} />
            </Route>
            <Route path="tasks">
                <Route path="create" element={<LazyRouteWrapper><FirstMileCreateTask /></LazyRouteWrapper>} />
                <Route path="filter" element={<LazyRouteWrapper><AdminTasksFilter /></LazyRouteWrapper>} />
                <Route path="zones" element={<LazyRouteWrapper><ZonesTasksCount /></LazyRouteWrapper>} />
                <Route path="zone_tasks/:zoneId" element={<LazyRouteWrapper><ZoneTasksList /></LazyRouteWrapper>} />
                <Route path="completed" element={<LazyRouteWrapper><TasksList /></LazyRouteWrapper>} />
                <Route path="warehouses" element={<LazyRouteWrapper><WarehousesOrdersCount /></LazyRouteWrapper>} />
                <Route path="warehouse_orders/:warehouseId" element={<LazyRouteWrapper><WarehouseOrdersList /></LazyRouteWrapper>} />
                <Route path=":taskId" element={<LazyRouteWrapper><TaskDetails /></LazyRouteWrapper>} />
                <Route path="archived/:taskId" element={<LazyRouteWrapper><TaskDetails /></LazyRouteWrapper>} />
                <Route path="orders" element={<LazyRouteWrapper><OrdersList /></LazyRouteWrapper>} />
                <Route path="order/:id" element={<LazyRouteWrapper><OrderDetails /></LazyRouteWrapper>} />
                <Route path="archived/order/:id" element={<LazyRouteWrapper><OrderDetails /></LazyRouteWrapper>} />
                <Route path="zone_orders_count" element={<LazyRouteWrapper><ZoneOrdersCount /></LazyRouteWrapper>} />
                <Route path="zone_orders" element={<LazyRouteWrapper><ZoneOrders /></LazyRouteWrapper>} />
                <Route path="*" element={<Navigate to="/errors/404" />} />
            </Route>
            <Route path="zones">
                <Route path="" element={<LazyRouteWrapper><ZoneList /></LazyRouteWrapper>} />
                <Route path="*" element={<Navigate to="/errors/404" />} />
            </Route>
            <Route path="qrcodes" element={<LazyRouteWrapper><Dashboard /></LazyRouteWrapper>} />
            <Route path="settings">
                <Route path="language" element={<LazyRouteWrapper><LanguageSetting /></LazyRouteWrapper>} />
                <Route path="whatsapp" element={<LazyRouteWrapper><WhatsappMessagesSettings /></LazyRouteWrapper>} />
                <Route path="*" element={<Navigate to="/errors/404" />} />
            </Route>
            <Route path="profile" element={<LazyRouteWrapper><Profile /></LazyRouteWrapper>} />
            <Route path="account">
                <Route path="" element={<Navigate to="info" />} />
                <Route path="info" element={<LazyRouteWrapper><MerchantAccountBasicInfo /></LazyRouteWrapper>} />
                <Route path="settings" element={<LazyRouteWrapper><MerchantAccountSettings /></LazyRouteWrapper>} />
                <Route path="plans" element={<LazyRouteWrapper><AccountPlans /></LazyRouteWrapper>} />
                <Route path="plan-checkout/:planId/:period" element={<LazyRouteWrapper><PlanCheckout /></LazyRouteWrapper>} />
                <Route path="subscription" element={<LazyRouteWrapper><AccountSubscription /></LazyRouteWrapper>} />
            </Route>
            <Route path="warehouse" element={<LazyRouteWrapper><Warehouse /></LazyRouteWrapper>} />
            <Route path="customizations" element={<LazyRouteWrapper><Customizations /></LazyRouteWrapper>} />
            <Route path="warehouse_guard" element={<LazyRouteWrapper><WarehouseGuard /></LazyRouteWrapper>} />
            <Route path="not-found" element={<LazyRouteWrapper><NotFound /></LazyRouteWrapper>} />
            <Route path="balance" element={<LazyRouteWrapper><AccountBalance /></LazyRouteWrapper>} />
            <Route path="*" element={<Navigate to="/errors/404" />} />
        </Route>
    </>
)

export default firstMileRoutes;
